/**
 * Compact Portfolio Stress Test resilience card — for Portfolio Doctor
 * and My Portfolio to link into /stress-test.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, ArrowUpRight, Lock, Play, SlidersHorizontal } from 'lucide-react';
import { Button } from '../ui/button';
import { fireChatAnalytics } from '../../lib/chatAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtRelative(iso, t) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.max(0, Math.round((now - then) / 60000));
  if (diffMin < 1) return t('stressTest.compact.justNow');
  if (diffMin < 60) return t('stressTest.compact.minutesAgo', { count: diffMin });
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return t('stressTest.compact.hoursAgo', { count: diffH });
  const diffD = Math.round(diffH / 24);
  return t('stressTest.compact.daysAgo', { count: diffD });
}

export default function StressResilienceCard({ isPremium, showActions = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(isPremium === false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/stress-test/latest`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 403) setLocked(true);
    }
  };

  useEffect(() => {
    if (isPremium === false) { setLocked(true); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremium]);

  const runModerate = async () => {
    setRunning(true);
    try {
      await axios.post(`${BACKEND_URL}/api/stress-test/run`, { preset: 'moderate' }, { withCredentials: true });
      await load();
      fireChatAnalytics('stress_test_preset_selected', '__stress_test__', { source: 'doctor', preset: 'moderate' });
      toast.success(t('stressTest.compact.runSuccess'));
    } catch (e) {
      toast.error(t('stressTest.failed'));
    } finally {
      setRunning(false);
    }
  };

  if (locked) {
    return (
      <div data-testid="stress-card-locked" className="rounded-2xl bg-gradient-to-br from-[#12355B] to-[#1a4574] p-5 text-white flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0"><Lock className="w-4 h-4" /></div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm">{t('stressTest.compact.lockedTitle')}</p>
          <p className="text-xs text-white/70">{t('stressTest.compact.lockedSubtitle')}</p>
        </div>
        <Button size="sm" data-testid="stress-card-upgrade" onClick={() => navigate('/pricing')}
          className="bg-[#45C04A] text-[#12355B] rounded-lg font-semibold">{t('stressTest.unlockCta')}</Button>
      </div>
    );
  }

  const empty = !data || data.empty;
  const score = data?.resilience?.score;
  const band = data?.resilience?.band;
  const vulnerable = data?.counts?.vulnerable ?? 0;
  const color = band === 'resilient' ? 'text-emerald-500' : band === 'moderate' ? 'text-amber-500' : 'text-red-500';
  const scenarioLabel = data?.scenario_label_key ? t(data.scenario_label_key) : null;
  const factorsSummary = data?.factors_summary;
  const relative = fmtRelative(data?.generated_at, t);

  return (
    <div data-testid="stress-resilience-card" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] p-5 shadow-card">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#12355B] to-[#1565C0] flex items-center justify-center flex-shrink-0">
          <Zap className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-primary font-bold text-lg text-[#12355B] dark:text-white">{t('stressTest.compact.title')}</h2>
          {empty ? (
            <p className="text-sm font-semibold text-[#12355B] dark:text-white">{t('stressTest.compact.notRunYet')}</p>
          ) : (
            <>
              <p className="text-sm font-semibold text-[#12355B] dark:text-white">
                <span data-testid="stress-card-score" className={`text-lg font-extrabold ${color}`}>{score}/100</span>
                {' · '}
                {t(`stressTest.resilience.band.${band}`)}
                {vulnerable > 0 && <span className="text-red-500 ml-2 text-xs">{t('stressTest.compact.vulnerableCount', { count: vulnerable })}</span>}
              </p>
              <p data-testid="stress-scenario-label" className="text-[11px] text-slate-400 mt-0.5">
                {scenarioLabel ? <span className="font-semibold text-slate-500 dark:text-slate-300">{scenarioLabel}</span> : null}
                {factorsSummary ? <span> · {factorsSummary}</span> : null}
                {relative ? <span> · {relative}</span> : null}
              </p>
            </>
          )}
        </div>
        <Button size="sm" variant="outline" data-testid="stress-card-open" onClick={() => {
          fireChatAnalytics('stress_test_viewed', '__stress_test__', { source: 'card' });
          navigate('/stress-test');
        }} className="rounded-lg">
          {empty ? t('stressTest.compact.runCta') : t('stressTest.compact.viewCta')}
          <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>

      {showActions && (
        <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex flex-wrap gap-2">
          <Button size="sm" data-testid="stress-run-moderate" onClick={runModerate} disabled={running}
            className="bg-[#12355B] hover:bg-[#1565C0] text-white rounded-lg text-xs h-9">
            <Play className="w-3.5 h-3.5 mr-1.5" />
            {running ? t('stressTest.compact.running') : t('stressTest.compact.runModerate')}
          </Button>
          <Button size="sm" variant="outline" data-testid="stress-customize" onClick={() => navigate('/stress-test?preset=custom')}
            className="rounded-lg text-xs h-9">
            <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" />
            {t('stressTest.compact.customize')}
          </Button>
        </div>
      )}
    </div>
  );
}
